import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { ExternalLink, Trash2 } from 'lucide-react';
import api from '../services/api';
import { invalidateClientes } from '../services/cachedCatalogs';
import { useConfirm, Tooltip, PhoneInput } from './ui';
import { useModalKeyboard } from '../hooks/useModalKeyboard';
import ClienteTipoToggle, { empresaEfectiva, TIPO_EMPRESA, TIPO_PERSONA } from './ClienteTipoToggle';

const clienteVacio = {
    nombre: '',
    tipo: TIPO_EMPRESA,
    empresa: '',
    email: '',
    telefono: '',
    notas: ''
};

const ClientesList = () => {
    const navigate = useNavigate();
    const confirm = useConfirm();
    const [clientes, setClientes] = useState([]);
    const [loading, setLoading] = useState(true);
    const [busqueda, setBusqueda] = useState('');

    // Modal
    const [showModal, setShowModal] = useState(false);
    const [formData, setFormData] = useState(clienteVacio);
    const [saving, setSaving] = useState(false);

    // Paginacion client-side
    const PAGE_SIZE_OPTIONS = [10, 25, 50, 100];
    const [pageSize, setPageSize] = useState(25);
    const [currentPage, setCurrentPage] = useState(1);

    useEffect(() => {
        cargarClientes();
    }, []);

    useEffect(() => { setCurrentPage(1); }, [busqueda, pageSize]);

    const cargarClientes = async () => {
        try {
            const response = await api.clientes.getAll();
            setClientes(response.data || []);
        } catch (err) {
            console.error('Error al cargar clientes:', err);
            toast.error('Error al cargar clientes');
        } finally {
            setLoading(false);
        }
    };

    const abrirModalNuevo = () => {
        setFormData(clienteVacio);
        setShowModal(true);
    };

    const cerrarModal = () => {
        setShowModal(false);
        setFormData(clienteVacio);
    };

    useModalKeyboard({ open: showModal, onClose: cerrarModal });

    const handleChange = (field, value) => {
        setFormData(prev => ({ ...prev, [field]: value }));
    };

    const esPersona = formData.tipo === TIPO_PERSONA;

    const handleGuardar = async () => {
        if (!formData.nombre.trim()) {
            toast.error('El nombre es obligatorio');
            return;
        }
        if (!esPersona && !formData.empresa.trim()) {
            toast.error('La empresa es obligatoria');
            return;
        }

        setSaving(true);
        try {
            await api.clientes.create({ ...formData, empresa: empresaEfectiva(formData) });
            toast.success('Cliente creado');
            invalidateClientes();
            cerrarModal();
            cargarClientes();
        } catch (err) {
            console.error('Error al guardar cliente:', err);
            toast.error('Error al guardar cliente');
        } finally {
            setSaving(false);
        }
    };

    const handleEliminar = async (id, nombre) => {
        const ok = await confirm({
            title: '¿Eliminar cliente?',
            message: <p>Vas a eliminar el cliente <strong>{nombre}</strong>. Esta acción no se puede deshacer.</p>,
            confirmText: 'Eliminar',
            danger: true,
        });
        if (!ok) return;
        try {
            await api.clientes.delete(id);
            invalidateClientes();
            toast.success('Cliente eliminado');
            cargarClientes();
        } catch (err) {
            toast.error('Error al eliminar');
        }
    };

    // Filtrar clientes por búsqueda
    const clientesFiltrados = clientes.filter(c => {
        const termino = busqueda.toLowerCase();
        return (
            c.nombre?.toLowerCase().includes(termino) ||
            c.empresa?.toLowerCase().includes(termino) ||
            c.email?.toLowerCase().includes(termino)
        );
    });

    const totalPaginas = Math.max(1, Math.ceil(clientesFiltrados.length / pageSize));
    const paginaActual = Math.min(currentPage, totalPaginas);
    const clientesPagina = clientesFiltrados.slice((paginaActual - 1) * pageSize, paginaActual * pageSize);

    if (loading) {
        return <div className="loading">Cargando clientes...</div>;
    }

    return (
        <>
            <div className="card">
                <div className="card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '12px' }}>
                    <h2>Clientes</h2>
                    <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
                        <input
                            className="input"
                            type="text"
                            placeholder="Buscar cliente..."
                            value={busqueda}
                            onChange={(e) => setBusqueda(e.target.value)}
                            style={{ width: '200px' }}
                        />
                        <button
                            className="btn neon"
                            onClick={abrirModalNuevo}
                        >
                            + Nuevo Cliente
                        </button>
                    </div>
                </div>

                <div className="card-body">
                    {clientesFiltrados.length === 0 ? (
                        <div className="empty-state">
                            <p>{busqueda ? 'No se encontraron clientes' : 'No hay clientes registrados'}</p>
                            <p style={{ color: 'var(--muted)', fontSize: '14px', marginTop: '8px' }}>
                                Los clientes te permiten autocompletar datos en las cotizaciones
                            </p>
                        </div>
                    ) : (
                        <div className="tableWrap">
                            <table className="galTable">
                                <thead>
                                    <tr>
                                        <th>Nombre</th>
                                        <th>Empresa</th>
                                        <th>Email</th>
                                        <th>Teléfono</th>
                                        <th style={{ width: '140px' }}>Acciones</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {clientesPagina.map((cliente) => (
                                        <tr
                                            key={cliente.id}
                                            onClick={() => navigate(`/clientes/${cliente.id}`)}
                                            style={{ cursor: 'pointer' }}
                                        >
                                            <td><strong>{cliente.nombre}</strong></td>
                                            <td>{cliente.empresa || '-'}</td>
                                            <td>{cliente.email || '-'}</td>
                                            <td>{cliente.telefono || '-'}</td>
                                            <td onClick={(e) => e.stopPropagation()}>
                                                <div style={{ display: 'flex', gap: '4px', justifyContent: 'flex-end' }}>
                                                    <Tooltip content="Ver detalle del cliente">
                                                        <button
                                                            className="btn-icon detalle"
                                                            onClick={() => navigate(`/clientes/${cliente.id}`)}
                                                            aria-label="Ver detalle"
                                                        >
                                                            <ExternalLink size={16} />
                                                        </button>
                                                    </Tooltip>
                                                    <Tooltip content="Eliminar cliente">
                                                        <button
                                                            className="btn-icon delete"
                                                            onClick={() => handleEliminar(cliente.id, cliente.nombre)}
                                                            aria-label="Eliminar"
                                                        >
                                                            <Trash2 size={16} />
                                                        </button>
                                                    </Tooltip>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}

                    {/* Paginacion client-side */}
                    {clientesFiltrados.length > 0 && (
                        <div className="admin-pagination">
                            <div className="admin-pagination-info">
                                Mostrando <strong>{(paginaActual - 1) * pageSize + 1}</strong>
                                {' – '}
                                <strong>{Math.min(paginaActual * pageSize, clientesFiltrados.length)}</strong>
                                {' de '}
                                <strong>{clientesFiltrados.length}</strong>
                            </div>
                            <div className="admin-pagination-controls">
                                <label className="admin-pagination-size">
                                    <span className="text-xs text-muted">Por página:</span>
                                    <select
                                        className="input"
                                        value={pageSize}
                                        onChange={(e) => setPageSize(parseInt(e.target.value, 10))}
                                    >
                                        {PAGE_SIZE_OPTIONS.map(n => <option key={n} value={n}>{n}</option>)}
                                    </select>
                                </label>
                                <div className="admin-pagination-buttons">
                                    <button
                                        type="button"
                                        className="btn ghost sm"
                                        disabled={paginaActual === 1}
                                        onClick={() => setCurrentPage(1)}
                                    >« Inicio</button>
                                    <button
                                        type="button"
                                        className="btn ghost sm"
                                        disabled={paginaActual === 1}
                                        onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                                    >‹</button>
                                    <span className="admin-pagination-current">
                                        Página <strong>{paginaActual}</strong> de <strong>{totalPaginas}</strong>
                                    </span>
                                    <button
                                        type="button"
                                        className="btn ghost sm"
                                        disabled={paginaActual === totalPaginas}
                                        onClick={() => setCurrentPage(p => Math.min(totalPaginas, p + 1))}
                                    >›</button>
                                    <button
                                        type="button"
                                        className="btn ghost sm"
                                        disabled={paginaActual === totalPaginas}
                                        onClick={() => setCurrentPage(totalPaginas)}
                                    >Fin »</button>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* Modal de Cliente */}
            {showModal && (
                <div className="modal-overlay" onClick={cerrarModal}>
                    <div className="modal modal-lg" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
                        <div className="modal-header">
                            <h3 className="modal-title">
                                Nuevo Cliente
                            </h3>
                            <button className="modal-close" onClick={cerrarModal}>&times;</button>
                        </div>

                        <div className="modal-body">
                            {/* Datos principales */}
                            <h4 className="modal-section-title">Datos del Cliente</h4>

                            <ClienteTipoToggle
                                value={formData.tipo}
                                onChange={(tipo) => handleChange('tipo', tipo)}
                                name="cliente-tipo-panel"
                            />

                            <div className="form-grid-2" style={{ marginTop: '16px' }}>
                                <label className="field">
                                    <span className="label">Nombre *</span>
                                    <input
                                        className="input"
                                        type="text"
                                        value={formData.nombre}
                                        onChange={(e) => handleChange('nombre', e.target.value)}
                                        placeholder={esPersona ? 'Nombre de la persona' : 'Nombre del contacto'}
                                    />
                                </label>

                                <label className="field">
                                    <span className="label">Empresa {esPersona ? '' : '*'}</span>
                                    <input
                                        className="input"
                                        type="text"
                                        value={empresaEfectiva(formData)}
                                        onChange={(e) => handleChange('empresa', e.target.value)}
                                        placeholder="Nombre de la empresa"
                                        disabled={esPersona}
                                    />
                                    {esPersona && (
                                        <span className="text-xs text-muted">
                                            En persona natural se usa el nombre como razón social.
                                        </span>
                                    )}
                                </label>

                                <label className="field">
                                    <span className="label">Email</span>
                                    <input
                                        className="input"
                                        type="email"
                                        value={formData.email}
                                        onChange={(e) => handleChange('email', e.target.value)}
                                        placeholder="correo@ejemplo.com"
                                    />
                                </label>

                                <label className="field">
                                    <span className="label">Teléfono</span>
                                    <PhoneInput
                                        value={formData.telefono}
                                        onChange={(v) => handleChange('telefono', v || '')}
                                    />
                                </label>
                            </div>

                            {/* Notas */}
                            <label className="field" style={{ marginTop: '24px' }}>
                                <span className="label">Notas</span>
                                <textarea
                                    className="input"
                                    rows={2}
                                    value={formData.notas}
                                    onChange={(e) => handleChange('notas', e.target.value)}
                                    placeholder="Notas adicionales sobre el cliente..."
                                />
                            </label>
                        </div>

                        <div className="modal-footer">
                            <button className="btn ghost" onClick={cerrarModal}>
                                Cancelar
                            </button>
                            <button className="btn neon" onClick={handleGuardar} disabled={saving}>
                                {saving ? 'Guardando...' : 'Crear Cliente'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default ClientesList;
